<template>
  <!-- Mobile: bottom drawer -->
  <UiDrawer
    v-if="windowWidth < 768"
    :model-value="modelValue"
    side="bottom"
    height="100dvh"
    @update:model-value="handleDrawerClose"
  >
    <div v-if="item" class="flex flex-col h-full">
      <!-- Header -->
      <div class="flex items-center justify-between pb-2 border-b border-border">
        <button
          v-if="authStep !== 'customize'"
          type="button"
          class="flex items-center justify-center w-9 h-9 rounded-full bg-border-light text-text-primary hover:bg-border transition-colors"
          aria-label="Back"
          @click="authStep = 'customize'"
        >
          <Icon name="arrow-left" :size="20" weight="regular" />
        </button>
        <span v-else class="w-9" />
        <button
          type="button"
          class="flex items-center justify-center w-9 h-9 rounded-full bg-border-light text-text-primary hover:bg-border transition-colors"
          aria-label="Close"
          @click="handleDrawerClose(false)"
        >
          <Icon name="x" :size="20" weight="regular" />
        </button>
      </div>

      <!-- ── Customize step ── -->
      <div v-if="authStep === 'customize'" class="flex-1 overflow-y-auto">
        <!-- Image -->
        <div
          v-if="displayImageUrl"
          class="w-full aspect-[2/1] bg-gray-100 rounded-xl overflow-hidden mb-4"
        >
          <img :src="displayImageUrl" :alt="item.title" class="w-full h-full object-cover" />
        </div>
        <div
          v-else
          class="w-full aspect-[2/1] bg-gray-100 rounded-xl flex items-center justify-center mb-4"
        >
          <Icon name="image" :size="48" weight="regular" color="#9ca3af" />
        </div>

        <div class="px-1">
          <h2 class="text-lg font-semibold text-text-primary m-0 mb-1">{{ item.title }}</h2>
          <p class="text-sm text-text-secondary m-0 mb-4">{{ formatPrice(headlinePrice) }}</p>

          <div v-if="isReadOnlyMode" class="space-y-4 pb-6">
            <p v-if="itemDescription" class="m-0 text-sm leading-6 text-text-secondary">
              {{ itemDescription }}
            </p>

            <div
              v-if="attributeValues.length > 0"
              class="rounded-2xl border border-border bg-background/70 p-4"
            >
              <p class="mb-3 text-sm font-medium text-text-primary">Available options</p>
              <div class="grid grid-cols-1 gap-2">
                <div
                  v-for="av in attributeValues"
                  :key="av.id"
                  class="flex items-center justify-between rounded-xl border border-border bg-white px-3 py-3"
                >
                  <span class="text-sm font-medium text-text-primary">{{ av.name }}</span>
                  <span class="text-sm font-semibold text-primary">
                    {{ formatPrice(av.menu_item_price) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="mb-4">
            <p class="mb-2 text-sm font-medium text-text-primary">Quantity</p>
            <div class="flex items-center gap-3">
              <button
                type="button"
                class="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-white text-text-primary hover:bg-gray-50 disabled:opacity-50"
                :disabled="quantity <= 1"
                aria-label="Decrease"
                @click="quantity = Math.max(1, quantity - 1)"
              >
                <Icon name="minus" :size="20" weight="bold" />
              </button>
              <span class="min-w-[2rem] text-center text-base font-semibold text-text-primary">
                {{ quantity }}
              </span>
              <button
                type="button"
                class="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-white text-text-primary hover:bg-gray-50"
                aria-label="Increase"
                @click="quantity += 1"
              >
                <Icon name="plus" :size="20" weight="bold" />
              </button>
            </div>
          </div>

          <!-- Choice of size / attributes -->
          <div v-if="!isReadOnlyMode && attributeValues.length > 0" class="mb-4">
            <p class="text-sm font-medium text-text-primary mb-2">Choice of Size</p>
            <div class="flex flex-col gap-2">
              <label
                v-for="av in attributeValues"
                :key="av.id"
                class="flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors"
                :class="
                  selectedAttributeId === String(av.id)
                    ? 'border-primary bg-primary/5'
                    : 'border-border bg-white hover:bg-gray-50'
                "
              >
                <input
                  v-model="selectedAttributeId"
                  type="radio"
                  :value="String(av.id)"
                  class="sr-only"
                />
                <span
                  class="flex-1 text-sm font-medium"
                  :class="
                    selectedAttributeId === String(av.id) ? 'text-primary' : 'text-text-primary'
                  "
                >
                  {{ av.name }} {{ formatPrice(av.menu_item_price) }}
                </span>
                <span
                  v-if="selectedAttributeId === String(av.id)"
                  class="w-5 h-5 rounded-full bg-primary flex items-center justify-center"
                >
                  <Icon name="check" :size="12" weight="bold" color="white" />
                </span>
              </label>
            </div>
          </div>

          <!-- Special notes -->
          <div v-if="!isReadOnlyMode" class="mb-4">
            <label class="text-sm font-medium text-text-primary mb-2 block">Special Notes</label>
            <textarea
              v-model="orderComment"
              class="w-full min-h-[80px] px-3 py-2 rounded-xl border border-border bg-white text-text-primary text-sm placeholder:text-text-tertiary resize-y"
              placeholder="Add a note"
              rows="3"
            />
          </div>
        </div>
      </div>

      <!-- ── Mobile step ── -->
      <div v-else-if="authStep === 'mobile'" class="flex-1 flex flex-col px-5 py-6">
        <Logo class="h-3 mb-6" small />
        <div class="flex-1">
          <h3 class="text-[24px] font-bold text-text-primary mb-6">Enter Mobile Number</h3>
          <MobileInput
            v-model="mobileNo"
            label="Enter Your Mobile Number"
            placeholder="Enter mobile number"
            :error="mobileError"
            :disabled="loading"
          />
          <p class="text-sm text-text-secondary mt-2">
            We'll send you a verification code to place your order
          </p>
        </div>
        <Button
          variant="primary"
          size="large"
          full-width
          :loading="loading"
          :disabled="!isMobileValid"
          @click="handleSendOtp"
        >
          Next
        </Button>
      </div>

      <!-- ── OTP step ── -->
      <div v-else-if="authStep === 'otp'" class="flex-1 flex flex-col px-5 py-6">
        <div class="flex-1">
          <Logo class="h-3 mb-6" small />
          <h3 class="text-[24px] font-bold text-text-primary mb-2">Enter Verification Code</h3>
          <p class="text-sm text-text-secondary mb-6">
            We sent a code to {{ formattedMobileDisplay }}
          </p>
          <OtpInput v-model="otp" :error="otpError" :disabled="loading" />
          <div class="mt-4">
            <button
              v-if="canResend"
              type="button"
              class="text-primary text-sm underline"
              @click="handleResendOtp"
            >
              Resend Code
            </button>
            <p v-else class="text-text-secondary text-sm m-0">
              Resend code in {{ formattedResendTime }}
            </p>
          </div>
        </div>
        <Button
          variant="primary"
          size="large"
          full-width
          :loading="loading"
          :disabled="otp.length < 4"
          @click="handleVerifyOtp"
        >
          Verify
        </Button>
      </div>

      <!-- ── Registration step ── -->
      <div v-else-if="authStep === 'register'" class="flex-1 overflow-y-auto px-1 pt-4">
        <h3 class="text-lg font-semibold text-text-primary m-0 mb-2">Complete Registration</h3>
        <p class="text-sm text-text-secondary m-0 mb-4">
          Please provide your details to complete your order
        </p>
        <Input
          v-model="regFirstName"
          label="First Name"
          placeholder="Enter first name"
          :error="regErrors.first_name"
          :disabled="loading"
        />
        <Input
          v-model="regLastName"
          label="Last Name"
          placeholder="Enter last name"
          :error="regErrors.last_name"
          :disabled="loading"
          class="mt-3"
        />
        <Input
          v-model="regEmail"
          label="Email (Optional)"
          type="email"
          placeholder="Enter email address"
          :error="regErrors.email"
          :disabled="loading"
          class="mt-3"
        />
        <Button
          variant="primary"
          size="large"
          full-width
          :loading="loading"
          :disabled="!regFirstName.trim() || !regLastName.trim()"
          class="mt-4"
          @click="handleCompleteRegistration"
        >
          Complete &amp; Order
        </Button>
      </div>

      <!-- ── Footer: only visible in customize step ── -->
      <div
        v-if="authStep === 'customize' && !isReadOnlyMode"
        class="border-t border-border bg-white pt-4"
        style="padding-bottom: max(1rem, env(safe-area-inset-bottom, 0))"
      >
        <div class="mb-4 flex items-end justify-start gap-4">
          <div class="min-w-0">
            <p class="m-0 text-sm text-text-secondary">Total Price</p>
            <p class="m-0 text-lg font-semibold text-primary">{{ formatPrice(totalPrice) }}</p>
          </div>
        </div>

        <!-- Edit mode: show Update button only -->
        <div v-if="isEditMode" class="mb-2 pt-1">
          <Button
            variant="primary"
            size="large"
            class="w-full rounded-xl"
            :loading="loading"
            @click="handleUpdateCartItem"
          >
            Update
          </Button>
        </div>

        <!-- Normal mode: Order Now (or Sign In) + Add to Cart -->
        <template v-else>
          <!-- Signed in + reservation (or table available for instant res): Order Now + cart -->
          <div
            v-if="canInteractWithOrder || canShowOrderNowWithInstantReservation"
            class="mb-2 flex min-w-0 items-center gap-4 pt-1"
          >
            <Button
              variant="primary"
              size="large"
              class="min-w-0 flex-1 rounded-xl"
              :loading="loading"
              @click="handleOrderNow"
            >
              Order Now
            </Button>
            <button
              type="button"
              class="flex items-center justify-center w-12 h-12 rounded-xl border-2 border-primary bg-white text-primary hover:bg-primary/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="loading"
              aria-label="Add to cart"
              @click="handleAddToCart"
            >
              <Icon name="shopping-cart" :size="24" weight="regular" />
            </button>
          </div>
          <p
            v-if="canInteractWithOrder || canShowOrderNowWithInstantReservation"
            class="text-xs text-text-secondary m-0"
          >
            This order will go directly to kitchen.
          </p>

          <!-- Not signed in: Sign In to Order + cart -->
          <div
            v-else-if="!authStore.isAuthenticated"
            class="mb-2 flex min-w-0 items-center gap-4 pt-1"
          >
            <Button
              variant="primary"
              size="large"
              class="min-w-0 flex-1 rounded-xl"
              @click="authStep = 'mobile'"
            >
              Order Now
            </Button>
            <button
              type="button"
              class="flex items-center justify-center w-12 h-12 rounded-xl border-2 border-primary bg-white text-primary hover:bg-primary/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="loading"
              aria-label="Add to cart"
              @click="handleAddToCart"
            >
              <Icon name="shopping-cart" :size="24" weight="regular" />
            </button>
          </div>

          <!-- Signed in but table booked by someone else (no reservation) -->
          <p
            v-else-if="tableBookedBySomeoneElse"
            class="text-xs text-center text-text-secondary m-0 py-1"
          >
            This table is booked by someone else. Please contact the waiter or restaurant.
          </p>

          <!-- Signed in but reservation/host check failed (session expired) -->
          <p v-else class="text-xs text-center text-text-secondary m-0 py-1">
            Ordering unavailable — your session may have expired.
          </p>
        </template>
      </div>
      <div
        v-else-if="authStep === 'customize' && isReadOnlyMode"
        class="border-t border-border bg-white pt-4"
        style="padding-bottom: max(1rem, env(safe-area-inset-bottom, 0))"
      >
        <router-link
          v-if="isSmartDineInEnabled"
          :to="activateOrderUrl"
          class="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-green px-5 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-primary/90"
          @click="handleDrawerClose(false)"
        >
          <PhLightning :size="18" weight="bold" class="shrink-0" />
          <span>Activate Order</span>
        </router-link>
      </div>
    </div>
  </UiDrawer>

  <!-- Desktop: centered modal (same inner content as drawer) -->
  <Modal
    v-else
    :model-value="modelValue"
    :show-actions="false"
    max-width="xl"
    @update:model-value="handleDrawerClose"
  >
    <div v-if="item" class="flex flex-col max-h-[80vh] min-h-[60vh] min-w-0">
      <!-- Header -->
      <div class="flex items-center justify-between pb-2 border-b border-border -mt-2 -mx-2 px-2">
        <button
          v-if="authStep !== 'customize'"
          type="button"
          class="flex items-center justify-center w-9 h-9 rounded-full bg-border-light text-text-primary hover:bg-border transition-colors"
          aria-label="Back"
          @click="authStep = 'customize'"
        >
          <Icon name="arrow-left" :size="20" weight="regular" />
        </button>
        <span v-else class="w-9" />
        <button
          type="button"
          class="flex items-center justify-center w-9 h-9 rounded-full bg-border-light text-text-primary hover:bg-border transition-colors"
          aria-label="Close"
          @click="handleDrawerClose(false)"
        >
          <Icon name="x" :size="20" weight="regular" />
        </button>
      </div>

      <!-- Body: scrollable content -->
      <div class="flex-1 overflow-y-auto overflow-x-hidden pt-4 pr-1">
        <!-- Customize step -->
        <div v-if="authStep === 'customize'">
          <div
            v-if="displayImageUrl"
            class="w-full aspect-[2/1] bg-gray-100 rounded-xl overflow-hidden mb-4"
          >
            <img :src="displayImageUrl" :alt="item.title" class="w-full h-full object-cover" />
          </div>
          <div
            v-else
            class="w-full aspect-[2/1] bg-gray-100 rounded-xl flex items-center justify-center mb-4"
          >
            <Icon name="image" :size="48" weight="regular" color="#9ca3af" />
          </div>
          <h2 class="text-lg font-semibold text-text-primary m-0 mb-1">{{ item.title }}</h2>
          <p class="text-sm text-text-secondary m-0 mb-4">{{ formatPrice(headlinePrice) }}</p>
          <div v-if="isReadOnlyMode" class="space-y-4 pb-2">
            <p v-if="itemDescription" class="m-0 text-sm leading-6 text-text-secondary">
              {{ itemDescription }}
            </p>

            <div
              v-if="attributeValues.length > 0"
              class="rounded-2xl border border-border bg-background/70 p-4"
            >
              <p class="mb-3 text-sm font-medium text-text-primary">Available options</p>
              <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div
                  v-for="av in attributeValues"
                  :key="av.id"
                  class="rounded-xl border border-border bg-white px-3 py-3"
                >
                  <p class="m-0 mb-1 text-sm font-medium text-text-primary">{{ av.name }}</p>
                  <p class="m-0 text-sm font-semibold text-primary">
                    {{ formatPrice(av.menu_item_price) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="mb-4">
            <p class="mb-2 text-sm font-medium text-text-primary">Quantity</p>
            <div class="flex items-center gap-3">
              <button
                type="button"
                class="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-white text-text-primary hover:bg-gray-50 disabled:opacity-50"
                :disabled="quantity <= 1"
                aria-label="Decrease"
                @click="quantity = Math.max(1, quantity - 1)"
              >
                <Icon name="minus" :size="20" weight="bold" />
              </button>
              <span class="min-w-[2rem] text-center text-base font-semibold text-text-primary">
                {{ quantity }}
              </span>
              <button
                type="button"
                class="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-white text-text-primary hover:bg-gray-50"
                aria-label="Increase"
                @click="quantity += 1"
              >
                <Icon name="plus" :size="20" weight="bold" />
              </button>
            </div>
          </div>
          <div v-if="!isReadOnlyMode && attributeValues.length > 0" class="mb-4">
            <p class="text-sm font-medium text-text-primary mb-2">Choice of Size</p>
            <div class="flex flex-col gap-2">
              <label
                v-for="av in attributeValues"
                :key="av.id"
                class="flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors"
                :class="
                  selectedAttributeId === String(av.id)
                    ? 'border-primary bg-primary/5'
                    : 'border-border bg-white hover:bg-gray-50'
                "
              >
                <input
                  v-model="selectedAttributeId"
                  type="radio"
                  :value="String(av.id)"
                  class="sr-only"
                />
                <span
                  class="flex-1 text-sm font-medium"
                  :class="
                    selectedAttributeId === String(av.id) ? 'text-primary' : 'text-text-primary'
                  "
                >
                  {{ av.name }} {{ formatPrice(av.menu_item_price) }}
                </span>
                <span
                  v-if="selectedAttributeId === String(av.id)"
                  class="w-5 h-5 rounded-full bg-primary flex items-center justify-center"
                >
                  <Icon name="check" :size="12" weight="bold" color="white" />
                </span>
              </label>
            </div>
          </div>
          <div v-if="!isReadOnlyMode" class="mb-4">
            <label class="text-sm font-medium text-text-primary mb-2 block">Special Notes</label>
            <textarea
              v-model="orderComment"
              class="w-full min-h-[80px] px-3 py-2 rounded-xl border border-border bg-white text-text-primary text-sm placeholder:text-text-tertiary resize-y"
              placeholder="Add a note"
              rows="3"
            />
          </div>
        </div>

        <!-- Mobile number step -->
        <div v-else-if="authStep === 'mobile'" class="flex flex-col gap-4">
          <Logo class="h-3" small />
          <h3 class="text-xl font-bold text-text-primary m-0">Enter Mobile Number</h3>
          <MobileInput
            v-model="mobileNo"
            label="Enter Your Mobile Number"
            placeholder="Enter mobile number"
            :error="mobileError"
            :disabled="loading"
          />
          <p class="text-sm text-text-secondary m-0">
            We'll send you a verification code to place your order
          </p>
          <Button
            variant="primary"
            size="large"
            full-width
            :loading="loading"
            :disabled="!isMobileValid"
            @click="handleSendOtp"
            >Next</Button
          >
        </div>

        <!-- OTP step -->
        <div v-else-if="authStep === 'otp'" class="flex flex-col gap-4">
          <Logo class="h-3" small />
          <h3 class="text-xl font-bold text-text-primary m-0">Enter Verification Code</h3>
          <p class="text-sm text-text-secondary m-0">
            We sent a code to {{ formattedMobileDisplay }}
          </p>
          <OtpInput v-model="otp" :error="otpError" :disabled="loading" />
          <button
            v-if="canResend"
            type="button"
            class="text-primary text-sm underline"
            @click="handleResendOtp"
          >
            Resend Code
          </button>
          <p v-else class="text-text-secondary text-sm m-0">
            Resend code in {{ formattedResendTime }}
          </p>
          <Button
            variant="primary"
            size="large"
            full-width
            :loading="loading"
            :disabled="otp.length < 4"
            @click="handleVerifyOtp"
            >Verify</Button
          >
        </div>

        <!-- Registration step -->
        <div v-else-if="authStep === 'register'" class="flex flex-col gap-4">
          <h3 class="text-lg font-semibold text-text-primary m-0">Complete Registration</h3>
          <p class="text-sm text-text-secondary m-0">
            Please provide your details to complete your order
          </p>
          <Input
            v-model="regFirstName"
            label="First Name"
            placeholder="Enter first name"
            :error="regErrors.first_name"
            :disabled="loading"
          />
          <Input
            v-model="regLastName"
            label="Last Name"
            placeholder="Enter last name"
            :error="regErrors.last_name"
            :disabled="loading"
          />
          <Input
            v-model="regEmail"
            label="Email (Optional)"
            type="email"
            placeholder="Enter email address"
            :error="regErrors.email"
            :disabled="loading"
          />
          <Button
            variant="primary"
            size="large"
            full-width
            :loading="loading"
            :disabled="!regFirstName.trim() || !regLastName.trim()"
            @click="handleCompleteRegistration"
          >
            Complete &amp; Order
          </Button>
        </div>
      </div>

      <!-- Footer: customize step only, fixed at bottom of modal -->
      <div
        v-if="authStep === 'customize' && !isReadOnlyMode"
        class="pt-4 border-t border-border mt-2 bg-white"
      >
        <div class="mb-4 flex items-end justify-start gap-6">
          <div class="min-w-0">
            <p class="m-0 text-sm text-text-secondary">Total Price</p>
            <p class="m-0 text-lg font-semibold text-primary">{{ formatPrice(totalPrice) }}</p>
          </div>
        </div>
        <div v-if="isEditMode">
          <Button
            variant="primary"
            size="large"
            class="w-full rounded-xl"
            :loading="loading"
            @click="handleUpdateCartItem"
            >Update</Button
          >
        </div>
        <template v-else>
          <div
            v-if="canInteractWithOrder || canShowOrderNowWithInstantReservation"
            class="mb-2 flex min-w-0 items-center gap-4 pt-1"
          >
            <Button
              variant="primary"
              size="large"
              class="min-w-0 flex-1 rounded-xl"
              :loading="loading"
              @click="handleOrderNow"
              >Order Now</Button
            >
            <button
              type="button"
              class="flex items-center justify-center w-12 h-12 rounded-xl border-2 border-primary bg-white text-primary hover:bg-primary/5 transition-colors disabled:opacity-50"
              :disabled="loading"
              aria-label="Add to cart"
              @click="handleAddToCart"
            >
              <Icon name="shopping-cart" :size="24" weight="regular" />
            </button>
          </div>
          <div
            v-else-if="!authStore.isAuthenticated"
            class="mb-2 flex min-w-0 items-center gap-4 pt-1"
          >
            <Button
              variant="primary"
              size="large"
              class="min-w-0 flex-1 rounded-xl"
              @click="authStep = 'mobile'"
            >
              Order Now
            </Button>
            <button
              type="button"
              class="flex items-center justify-center w-12 h-12 rounded-xl border-2 border-primary bg-white text-primary hover:bg-primary/5 transition-colors disabled:opacity-50"
              :disabled="loading"
              aria-label="Add to cart"
              @click="handleAddToCart"
            >
              <Icon name="shopping-cart" :size="24" weight="regular" />
            </button>
          </div>
          <p
            v-else-if="tableBookedBySomeoneElse"
            class="text-xs text-center text-text-secondary m-0 py-1"
          >
            This table is booked by someone else. Please contact the waiter or restaurant.
          </p>
          <p v-else class="text-xs text-center text-text-secondary m-0 py-1">
            Ordering unavailable — your session may have expired.
          </p>
        </template>
      </div>
      <div
        v-else-if="isReadOnlyMode"
        class="pt-4 border-t border-border mt-2 bg-white"
      >
        <router-link
          v-if="isSmartDineInEnabled"
          :to="activateOrderUrl"
          class="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-green px-5 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-primary/90"
          @click="handleDrawerClose(false)"
        >
          <PhLightning :size="18" weight="bold" class="shrink-0" />
          <span>Activate Order</span>
        </router-link>
      </div>
    </div>
  </Modal>

  <Modal
    :model-value="showExpiredConfirmation"
    title="Start New Session"
    confirm-text="Start"
    cancel-text="Cancel"
    max-width="sm"
    @update:model-value="handleExpiredCancel"
    @confirm="handleExpiredConfirm"
    @close="handleExpiredCancel"
  >
    <p>Your haven't active reservation yet or expired your previous reservation session. Would you like to start a new one to continue ordering?</p>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useWindowWidth } from '@/composables/core/useWindowWidth'
import UiDrawer from '@/components/common/UiDrawer.vue'
import Modal from '@/components/common/Modal.vue'
import Button from '@/components/common/Button.vue'
import Input from '@/components/common/Input.vue'
import MobileInput from '@/components/common/MobileInput.vue'
import OtpInput from '@/components/common/OtpInput.vue'
import { PhLightning } from '@phosphor-icons/vue'
import Icon from '@/components/atoms/Icon.vue'
import Logo from '@/components/atoms/Logo.vue'
import { useOrder } from '@/composables/features/useOrder'
import { useCurrency } from '@/composables/core/useCurrency'
import { useAuthStore } from '@/stores/auth'
import { useRestaurantStore } from '@/stores/restaurant'
import { useMobileValidation } from '@/composables/features/useMobileValidation'
import { useResendTimer } from '@/composables/features/useResendTimer'
import { useNotifications } from '@/composables/core/useNotifications'
import { useReservationSessionGuard } from '@/composables/features/useReservationSessionGuard'
import { authApi } from '@/api/auth'
import { reservationApi } from '@/api/reservation'
import { parseApiError } from '@/utils/errorHandler'
import type { MenuItem, AttributeValue, OrderItem } from '@/interfaces'

interface Props {
  modelValue: boolean
  item: MenuItem | null
  /** Pre-selected attribute when opened from a card price button */
  initialVariant?: AttributeValue | null
  /** When set, drawer is in edit-cart mode: pre-fill from this item and show Update button */
  editingCartItem?: OrderItem | null
  tenantId: string
  activeReservationId?: string
  imageUrl?: string
  readOnly?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'add-to-cart': [item: OrderItem]
  'update-cart-item': [item: OrderItem]
  /** Fired when user logs in through the drawer but the table is already booked by someone else */
  'table-unavailable': []
}>()

// ── Step state ────────────────────────────────────────────────────────────────
type AuthStep = 'customize' | 'mobile' | 'otp' | 'register'
const authStep = ref<AuthStep>('customize')

// ── Reactive dependencies ─────────────────────────────────────────────────────
const loading = ref<boolean>(false)
/** false when check_active_reservation_new returns error/422 */
const reservationValid = ref<boolean>(true)
/** Guard flag for order interaction; set false when the reservation session is no longer active. */
const isHost = ref<boolean>(true)
/** true when authenticated but table is already booked by someone else (no active reservation) */
const tableBookedBySomeoneElse = ref<boolean>(false)

const route = useRoute()
const router = useRouter()
const { windowWidth } = useWindowWidth()
const authStore = useAuthStore()
const restaurantStore = useRestaurantStore()
const { confirmOrderItem } = useOrder()
const { formatItemPrice, convertToDefaultCurrency } = useCurrency()
const { showError, showSuccess } = useNotifications()
const { validateReservationStillActive } = useReservationSessionGuard()

/** Falls back to authStore when the parent hasn't passed reservation_id via URL */
const resolvedReservationId = computed((): string => {
  return props.activeReservationId ?? authStore.activeReservationId ?? ''
})

/** True only when authenticated AND both reservation + host checks pass */
const canInteractWithOrder = computed((): boolean => {
  return (
    authStore.isAuthenticated &&
    !!resolvedReservationId.value &&
    reservationValid.value &&
    isHost.value
  )
})

/** Logged in, no reservation yet, table available: show Order Now and create instant reservation on click */
const canShowOrderNowWithInstantReservation = computed((): boolean => {
  return (
    authStore.isAuthenticated &&
    !resolvedReservationId.value &&
    !tableBookedBySomeoneElse.value &&
    !!(route.query.table_no as string) &&
    !!props.tenantId
  )
})

const isReadOnlyMode = computed(() => props.readOnly === true)

const isSmartDineInEnabled = computed(() => {
  const status = restaurantStore.currentRestaurant?.smart_dine_in_status
  return status !== 0 && status !== false
})

const activateOrderUrl = computed(() => {
  return { path: route.path, query: { ...route.query, activate_order: 'true' } }
})

// ── Mobile input + validation ─────────────────────────────────────────────────
const {
  mobileNo,
  mobileError,
  formattedMobileNo,
  formattedMobileDisplay,
  isValid: isMobileValid,
  handleMobileInputUpdate,
  validateMobile,
  setError: setMobileError,
} = useMobileValidation()

const { formattedResendTime, canResend, startCooldown } = useResendTimer(120)

// track whether the account already exists so we know the post-OTP path
const accountExists = ref<boolean | null>(null)

// ── OTP ───────────────────────────────────────────────────────────────────────
const otp = ref<string>('')
const otpError = ref<string>('')

// ── Registration form ─────────────────────────────────────────────────────────
const regFirstName = ref<string>('')
const regLastName = ref<string>('')
const regEmail = ref<string>('')
const regErrors = ref<Record<string, string>>({})

// sync mobile input
watch(mobileNo, val => handleMobileInputUpdate(val))

// auto-verify when 4 digits entered
watch(otp, val => {
  if (val.length > 0) otpError.value = ''
  if (val.length === 4) void handleVerifyOtp()
})

// clear field-level reg errors as user types
watch(regFirstName, () => {
  if (regErrors.value.first_name) delete regErrors.value.first_name
})
watch(regLastName, () => {
  if (regErrors.value.last_name) delete regErrors.value.last_name
})
watch(regEmail, () => {
  if (regErrors.value.email) delete regErrors.value.email
})

// ── OTP Handlers ──────────────────────────────────────────────────────────────

async function handleSendOtp(): Promise<void> {
  if (!validateMobile()) return
  loading.value = true
  try {
    try {
      // checkCustomerMobile also generates OTP for existing accounts (legacy behaviour)
      const check = await authApi.checkCustomerMobile({ mobile_no: formattedMobileNo.value })
      if (check?.error) throw new Error(parseApiError(check).message)
      accountExists.value = true
      // OTP already sent by checkCustomerMobile — go straight to OTP step
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number } }
      if (ax.response?.status === 422) {
        // New user — explicitly request OTP
        accountExists.value = false
        await authStore.sendOTP({ mobile_no: formattedMobileNo.value, account_exists: 'No' })
      } else {
        throw err
      }
    }
    authStep.value = 'otp'
    startCooldown()
    showSuccess('Verification code sent!')
  } catch (error: unknown) {
    setMobileError(parseApiError(error).message)
  } finally {
    loading.value = false
  }
}

async function handleVerifyOtp(): Promise<void> {
  if (otp.value.length < 4) return
  loading.value = true
  try {
    // Check OTP validity first
    const check = await authApi.checkOtpValidity({
      mobile_no: formattedMobileNo.value,
      otp: otp.value,
    })
    if (check?.error) throw new Error(parseApiError(check).message)

    if (accountExists.value === true) {
      // Existing user: complete authentication
      await authStore.verifyOTP({ mobile_no: formattedMobileNo.value, otp: otp.value })
      showSuccess('Verified!')
      // Place the single item right away
      await confirmSingleItemAfterAuth(formattedMobileNo.value)
    } else {
      // New user: OTP is valid, go to registration form
      authStep.value = 'register'
    }
  } catch (error: unknown) {
    otpError.value = parseApiError(error).message
  } finally {
    loading.value = false
  }
}

async function handleResendOtp(): Promise<void> {
  if (!canResend.value) return
  otp.value = ''
  otpError.value = ''
  await handleSendOtp()
}

// ── Registration Handler ──────────────────────────────────────────────────────

async function handleCompleteRegistration(): Promise<void> {
  regErrors.value = {}
  if (!regFirstName.value.trim()) {
    regErrors.value.first_name = 'First name is required'
    return
  }
  if (!regLastName.value.trim()) {
    regErrors.value.last_name = 'Last name is required'
    return
  }
  loading.value = true
  try {
    await authStore.checkCustomerInfoValidity({
      mobile_no: formattedMobileNo.value,
      first_name: regFirstName.value.trim(),
      last_name: regLastName.value.trim(),
      ...(regEmail.value.trim() ? { email: regEmail.value.trim() } : {}),
    })
    showSuccess('Registration complete!')
    await confirmSingleItemAfterAuth(formattedMobileNo.value)
  } catch (error: unknown) {
    const msg = parseApiError(error).message
    if (msg.toLowerCase().includes('first_name') || msg.toLowerCase().includes('first name')) {
      regErrors.value.first_name = msg
    } else if (msg.toLowerCase().includes('last_name') || msg.toLowerCase().includes('last name')) {
      regErrors.value.last_name = msg
    } else if (msg.toLowerCase().includes('email')) {
      regErrors.value.email = msg
    } else {
      showError(msg)
    }
  } finally {
    loading.value = false
  }
}

/**
 * After a successful OTP/registration, confirm this single item and close the drawer.
 * @param fallbackMobileNo - the mobile number entered in the OTP step; used when
 *   authStore.user?.mobile_no is not populated yet after fresh registration.
 */
async function confirmSingleItemAfterAuth(fallbackMobileNo?: string): Promise<void> {
  loading.value = true
  try {
    let resId: string | null = resolvedReservationId.value || null
    if (!resId) {
      resId = await ensureInstantReservationForLoggedInUser(
        authStore.user?.mobile_no ?? fallbackMobileNo
      )
      if (!resId) return
    }

    const built = buildOrderItem()
    if (!built) return
    const orderItem: OrderItem = {
      ...built,
      ...(resId ? { active_reservation_id: resId } : {}),
    }
    const success = await confirmOrderItem(orderItem)
    if (success) {
      resetAuthState()
      emit('update:modelValue', false)
    }
  } catch (err: unknown) {
    showError(parseApiError(err).message)
  } finally {
    loading.value = false
  }
}

/** Mirrors the table-availability check used in useUnifiedOrderFlow */
function isTableAvailableFromResponse(response: unknown): boolean {
  if (response === true || response === 'true') return true
  if (response && typeof response === 'object') {
    const obj = response as Record<string, unknown>
    if (obj.error) return false
    const raw = obj.data ?? obj.available ?? obj.table_available
    if (raw === true || raw === 'true') return true
    if ('show_table_available' in obj) return obj.show_table_available === false
  }
  return false
}

/**
 * Create instant registered reservation when user is logged in, has table_no in URL, and table is available.
 * Updates authStore and URL. Returns reservation id or null on failure.
 */
async function ensureInstantReservationForLoggedInUser(mobileNo?: string): Promise<string | null> {
  const tableNo = route.query.table_no as string | undefined
  const mobile = mobileNo ?? authStore.user?.mobile_no
  if (!tableNo || !props.tenantId || !mobile) {
    showError('No active reservation found. Please ensure your profile has a mobile number.')
    return null
  }

  const availabilityRes = await restaurantStore.checkTableAvailability({
    tenant_id: props.tenantId,
    table_no: tableNo,
  })
  const isAvailable = isTableAvailableFromResponse(availabilityRes)
  if (!isAvailable) {
    tableBookedBySomeoneElse.value = true
    emit('table-unavailable')
    return null
  }

  const reservationResponse = await reservationApi.makeInstantRegisteredReservation({
    mobile_no: mobile,
    tenant_id: props.tenantId,
    table_no: tableNo,
  })
  if (reservationResponse.error) {
    showError(parseApiError(reservationResponse).message)
    return null
  }
  const reservation = ((reservationResponse as Record<string, unknown>).data ??
    reservationResponse) as Record<string, unknown>
  const resId =
    reservation?.active_reservation_id != null
      ? String(reservation.active_reservation_id)
      : reservation?.id != null
        ? String(reservation.id)
        : ''
  if (!resId) {
    showError('Failed to create reservation.')
    return null
  }

  authStore.setActiveReservation(resId, { tenantId: props.tenantId, tableNo })
  const cleanQuery: Record<string, string> = {
    ...(route.query as Record<string, string>),
    reservation_id: resId,
  }
  delete cleanQuery.drawer
  await router.replace({
    path: route.path,
    query: cleanQuery,
  })

  reservationValid.value = true
  isHost.value = true
  return resId
}

// ── Customize step helpers ────────────────────────────────────────────────────

const isEditMode = computed(() => !!props.editingCartItem)

const quantity = ref<number>(1)
const orderComment = ref<string>('')
const selectedAttributeId = ref<string>('')

const displayImageUrl = computed(() => props.imageUrl || props.item?.image_url)

const attributeValues = computed((): AttributeValue[] => {
  if (!props.item?.item_attributes?.length) return []
  const list: AttributeValue[] = []
  props.item.item_attributes.forEach(attr => {
    if (attr.attribute_values?.length) list.push(...attr.attribute_values)
  })
  return list
})

const basePrice = computed(() => props.item?.price ?? 0)
const headlinePrice = computed(() => {
  if (attributeValues.value.length > 0) {
    return Math.min(...attributeValues.value.map(value => value.menu_item_price))
  }
  return basePrice.value
})
const itemDescription = computed(() => props.item?.description?.trim() ?? '')

const selectedAttribute = computed((): AttributeValue | null => {
  if (!selectedAttributeId.value || !attributeValues.value.length) return null
  return attributeValues.value.find(v => String(v.id) === selectedAttributeId.value) ?? null
})

const unitPrice = computed(() => selectedAttribute.value?.menu_item_price ?? basePrice.value)
const totalPrice = computed(() => unitPrice.value * quantity.value)

function formatPrice(price: number | null | undefined): string {
  return formatItemPrice(price, props.item?.currency_code)
}

function buildOrderItem(): OrderItem | null {
  if (!props.item) return null

  const currencyCode = props.item.currency_code
  const rawUnitPrice = unitPrice.value
  const rawDiscount = selectedAttribute.value?.menu_item_discount ?? props.item.discount ?? 0

  const convertedUnitPrice = convertToDefaultCurrency(rawUnitPrice, currencyCode)
  if (convertedUnitPrice === null) {
    showError(
      `Currency exchange rate not found for ${currencyCode || 'foreign currency'}. Cannot add to cart.`
    )
    return null
  }

  const convertedDiscount =
    rawDiscount > 0 ? (convertToDefaultCurrency(rawDiscount, currencyCode) ?? rawDiscount) : 0

  const itemId = (props.item as any)?.item_id ?? props.item.id
  const attrId = selectedAttribute.value ? String(selectedAttribute.value.id) : ''
  const total = convertedUnitPrice * quantity.value

  return {
    item_id: String(itemId),
    item_name: props.item.title,
    count: quantity.value,
    item_amount: convertedUnitPrice,
    total_amount: total,
    amount: convertedUnitPrice,
    item_discount: convertedDiscount,
    ...(orderComment.value ? { order_comment: orderComment.value } : {}),
    attribute_id: attrId,
    attribute_name: selectedAttribute.value?.name ?? '',
    ...(resolvedReservationId.value ? { active_reservation_id: resolvedReservationId.value } : {}),
  }
}

async function handleAddToCart(): Promise<void> {
  if (!props.item || isReadOnlyMode.value) return
  const orderItem = buildOrderItem()
  if (!orderItem) return

  loading.value = true
  try {
    emit('add-to-cart', orderItem)
    emit('update:modelValue', false)
  } finally {
    loading.value = false
  }
}

const showExpiredConfirmation = ref<boolean>(false)

async function handleExpiredConfirm(): Promise<void> {
  showExpiredConfirmation.value = false
  loading.value = true
  try {
    const resId = await ensureInstantReservationForLoggedInUser()
    if (!resId) return

    const built = buildOrderItem()
    if (!built) return
    const orderItem: OrderItem = {
      ...built,
      active_reservation_id: resId,
    }
    const success = await confirmOrderItem(orderItem)
    if (success) {
      resetAuthState()
      emit('update:modelValue', false)
    }
  } finally {
    loading.value = false
  }
}

function handleExpiredCancel(): void {
  showExpiredConfirmation.value = false
}

async function handleOrderNow(): Promise<void> {
  if (!props.item || isReadOnlyMode.value) return

  let resId: string | null = resolvedReservationId.value || null

  // If there is an active reservation ID, validate if it is still active first
  if (resId) {
    const numId = Number(resId)
    if (!Number.isNaN(numId)) {
      loading.value = true
      try {
        const validation = await validateReservationStillActive(numId)
        if (!validation.ok) {
          reservationValid.value = false
          isHost.value = false
          showExpiredConfirmation.value = true
          return
        }
      } finally {
        loading.value = false
      }
    }
  }

  // If no reservation exists, prompt confirmation before auto-creating
  if (!resId && canShowOrderNowWithInstantReservation.value) {
    showExpiredConfirmation.value = true
    return
  }

  if (!resId) return

  loading.value = true
  try {
    const built = buildOrderItem()
    if (!built) return
    const orderItem: OrderItem = {
      ...built,
      active_reservation_id: resId,
    }
    const success = await confirmOrderItem(orderItem)
    if (success) emit('update:modelValue', false)
  } finally {
    loading.value = false
  }
}

function handleUpdateCartItem(): void {
  if (!props.item || isReadOnlyMode.value) return
  const orderItem = buildOrderItem()
  if (!orderItem) return
  emit('update-cart-item', orderItem)
  emit('update:modelValue', false)
}

// ── Drawer close / reset ──────────────────────────────────────────────────────

function resetAuthState(): void {
  authStep.value = 'customize'
  otp.value = ''
  otpError.value = ''
  accountExists.value = null
  regFirstName.value = ''
  regLastName.value = ''
  regEmail.value = ''
  regErrors.value = {}
}

function handleDrawerClose(val: boolean): void {
  resetAuthState()
  emit('update:modelValue', val)
}

// ── Watchers ──────────────────────────────────────────────────────────────────

// On drawer open: check active reservation (tablemates with PIN share the same reservation); on close: reset flags
watch(
  () => props.modelValue,
  async open => {
    if (!open) {
      reservationValid.value = true
      isHost.value = true
      tableBookedBySomeoneElse.value = false
      return
    }

    if (isReadOnlyMode.value) {
      authStep.value = 'customize'
      reservationValid.value = true
      isHost.value = true
      tableBookedBySomeoneElse.value = false
      return
    }

    // Authenticated but no reservation — check if the table is booked by someone else
    if (authStore.isAuthenticated && !resolvedReservationId.value) {
      const tableNo = route.query.table_no as string | undefined
      if (tableNo && props.tenantId) {
        try {
          const availRes = await restaurantStore.checkTableAvailability({
            tenant_id: props.tenantId,
            table_no: tableNo,
          })
          tableBookedBySomeoneElse.value = !isTableAvailableFromResponse(availRes)
        } catch {
          tableBookedBySomeoneElse.value = false
        }
      }
      return
    }

    // Only run reservation checks when authenticated and has a reservation
    if (!authStore.isAuthenticated || !resolvedReservationId.value) {
      reservationValid.value = true
      isHost.value = true
      return
    }
    const id = Number(resolvedReservationId.value)
    if (Number.isNaN(id)) return

    const validation = await validateReservationStillActive(id)
    if (!validation.ok) {
      reservationValid.value = false
      isHost.value = false
      showError(validation.message)
      return
    }
    reservationValid.value = true
    isHost.value = true
  }
)

// Pre-fill when editing cart item, or reset when item/variant changes
watch(
  () => [props.modelValue, props.item, props.initialVariant, props.editingCartItem] as const,
  ([open, newItem, initial, editing]) => {
    if (!open || !newItem) return
    authStep.value = 'customize'
    if (editing) {
      quantity.value = editing.count
      selectedAttributeId.value = editing.attribute_id || ''
      orderComment.value = editing.order_comment ?? ''
      return
    }
    quantity.value = 1
    orderComment.value = ''
    const attrs: AttributeValue[] = []
    newItem.item_attributes?.forEach(attr => {
      if (attr.attribute_values?.length) attrs.push(...attr.attribute_values)
    })
    if (initial) {
      selectedAttributeId.value = String(initial.id)
    } else if (attrs.length > 0) {
      selectedAttributeId.value = String(attrs[0]?.id ?? '')
    } else {
      selectedAttributeId.value = ''
    }
  },
  { immediate: true }
)
</script>
